// AI prompting panel — entry point.
//
// Composes:
// 1. The editable-prompts stack at the top (one card per registered key in
//    `server/prompts/catalog.ts`). Each card surfaces "Suggest edit"
//    (anyone with panel access) and, for trainer+ / owner+ / platform_*,
//    "Edit directly" + "Revert to default".
// 2. A "Suggestions (count)" pill in the header for apply-role users that
//    expands an inline review tray with Accept / Reject / Merge per
//    suggestion.
// 3. The original static `PROMPT_SURFACES` reference table at the bottom
//    (kept until every prompt site is migrated to the catalog).
//
// See docs/planning/ai_prompting_suggest_edit.md for the locked plan.

import { useMemo, useState } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { useAuth } from "@/contexts/AuthContext";
import { AI_PROMPTING_APPLY_ROLES } from "@/lib/aiPrompting/roles";
import { EditablePromptCard } from "./aiPrompting/EditablePromptCard";
import { SuggestionDiffRow } from "./aiPrompting/SuggestionDiffRow";
import { PromptSurfacesReference } from "./aiPrompting/PromptSurfacesReference";
import { useAiPromptingPanelData } from "./aiPrompting/useAiPromptingPanelData";

export function AiPromptingPanel() {
  const auth = useAuth();
  // Use `realHasRole` (ignores impersonation) for apply gating so a dev
  // simulating a viewer can't accept their own suggestions cross-org.
  const canApply = useMemo(() => auth.realHasRole(...AI_PROMPTING_APPLY_ROLES), [auth]);
  const { data, loading, error, refresh, saveOverride, revertOverride, createSuggestion, decide } =
    useAiPromptingPanelData(true);
  const [trayOpen, setTrayOpen] = useState(false);

  const openCount = data.suggestions.length;

  // Container/padding doctrine \u00a7K1+K5: panel root is flat \u2014 no saturated
  // violet plate (was `rounded-2xl border-violet-200 bg-violet-50 p-5`).
  // Header row + SectionCard children sit directly on the shell gradient.
  // See docs/planning/container_padding_pass.md \u00a7K.
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "ap-surface" }} className="w-full text-left space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">AI Prompting</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-zinc-300">
            Edit the prompts that drive your org&rsquo;s AI features. Viewers can &ldquo;Suggest edit&rdquo; \u2014 trainers and owners can publish overrides or accept suggestions.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canApply && openCount > 0 ? (
            <button
              type="button"
              onClick={() => setTrayOpen((o) => !o)}
              className="text-[11px] rounded-full border border-amber-400 bg-amber-100 px-2 py-1 font-semibold text-amber-900 hover:bg-amber-200 animate-pulse dark:border-amber-600 dark:bg-amber-950/50 dark:text-amber-100"
              aria-pressed={trayOpen}
            >
              {openCount} suggestion{openCount === 1 ? "" : "s"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => void refresh()}
            disabled={loading}
            className="text-[11px] rounded-lg border border-gray-300 bg-white px-2 py-1 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
          >
            {loading ? "Loading\u2026" : "Refresh"}
          </button>
        </div>
      </header>

      {error ? (
        <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </p>
      ) : null}

      {canApply && trayOpen && openCount > 0 ? (
        <section
          {...{ [FIREFLY_TARGET_ATTR]: "ap-review-queue" }}
          className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 space-y-3 dark:border-amber-700 dark:bg-amber-950/30"
        >
          <header className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
              Pending suggestions ({openCount})
            </h3>
            <button
              type="button"
              onClick={() => setTrayOpen(false)}
              className="text-[11px] text-amber-800 underline-offset-2 hover:underline dark:text-amber-200"
            >
              Close
            </button>
          </header>
          <ul className="space-y-3">
            {data.suggestions.map((s) => (
              <SuggestionDiffRow key={s.id} suggestion={s} onDecide={decide} />
            ))}
          </ul>
        </section>
      ) : canApply ? (
        <section
          {...{ [FIREFLY_TARGET_ATTR]: "ap-review-queue" }}
          className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/30 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/15"
        >
          <p className="text-xs text-amber-900/80 dark:text-amber-100/80">
            No pending suggestions — the review tray opens from the pill above when edits await approval.
          </p>
        </section>
      ) : null}

      <section {...{ [FIREFLY_TARGET_ATTR]: "ap-prompt-stack" }} className="space-y-3">
        {data.prompts.length === 0 && !loading && !error ? (
          <p className="text-sm text-gray-600 dark:text-zinc-300">
            No editable prompts are registered yet.
          </p>
        ) : null}
        {data.prompts.map((p, index) => (
          <EditablePromptCard
            key={p.prompt_key}
            prompt={p}
            canApply={canApply}
            suggestFireflyTargetId={index === 0 ? "ap-suggest-edit" : undefined}
            onSaveOverride={(text) => saveOverride(p.prompt_key, text)}
            onRevertOverride={() => revertOverride(p.prompt_key)}
            onCreateSuggestion={(text, note) => createSuggestion(p.prompt_key, text, note)}
            onRefresh={() => void refresh()}
          />
        ))}
      </section>

      <PromptSurfacesReference />
    </div>
  );
}
